<template>
	<div class="geography-investing gray-bar-top app clearfix">
		<h3 class="module-title">
			The New Geography of Investing ®
			<disclosure-superscript class="detail-page" anchor="newGeo" :statistics="['NEWGEO']" />
		</h3>
		<div class="row">
			<div class="col-md-6 clearfix">
				<div class="tab-container">
					<a
						class="sub-header"
						:class="{active: viewDomicile}"
						@click="changeTab('domicile')"
					>Equity Portion Breakdown by Domicile</a>
					<a
						class="sub-header divider-line"
						:class="{active: viewRevenue}"
						@click="changeTab('revenue')"
					>Equity Portion Breakdown by Revenue</a>
				</div>
				<div v-if="viewDomicile" class="row">
					<div class="col-md-4 col-sm-4 col-xs-4">
						<img :src="domicileChart">
					</div>
					<div class="col-md-8 col-sm-8 col-xs-8">
						<table>
							<thead>
								<tr>
									<td class="square" />
									<td class="first-col">Regions</td>
									<td class="second-col align-right">Fund</td>
									<td class="align-right">
										{{ indexName }}
										<disclosure-superscript
											class="detail-page"
											anchor="newGeo_indexId"
											:statistics="[indexId]"
										/>
										<i-ball-modal :tag="indexId" />
									</td>
								</tr>
							</thead>
							<tbody>
								<tr v-for="(row, index) in equityByDomicile" :key="index">
									<td class="square">
										<div class="colored-square" :class="getColorClass(row.RegionName)" />
									</td>
									<td>{{ row.RegionName }}</td>
									<td class="data">{{ $format.rawPercent(row.Value, 1) }}</td>
									<td class="data">{{ $format.rawPercent(row.IndexValue, 1) }}</td>
								</tr>
							</tbody>
						</table>
						<div class="sub-info adjust-date">% of net assets as of {{ asOfDate }} (updated monthly)</div>
					</div>
				</div>
				<div v-if="viewRevenue" class="row">
					<div class="col-md-4 col-sm-4 col-xs-4">
						<img :src="revenueChart">
					</div>
					<div class="col-md-8 col-sm-8 col-xs-8">
						<table>
							<thead>
								<tr>
									<td class="square" />
									<td class="first-col">Regions</td>
									<td class="second-col align-right">Fund</td>
									<td class="align-right">
										{{ indexName }}
										<disclosure-superscript
											class="detail-page"
											anchor="newGeo_indexId"
											:statistics="[indexId]"
										/>
										<i-ball-modal :tag="indexId" />
									</td>
								</tr>
							</thead>
							<tbody>
								<tr v-for="(row, index) in equityByRevenue" :key="index">
									<td class="square">
										<div class="colored-square" :class="getColorClass(row.RegionName)" />
									</td>
									<td>{{ row.RegionName }}</td>
									<td class="data">{{ $format.rawPercent(row.Value, 1) }}</td>
									<td class="data">{{ $format.rawPercent(row.IndexValue, 1) }}</td>
								</tr>
							</tbody>
						</table>
						<div class="sub-info adjust-date">% of net assets as of {{ asOfDate }} (updated monthly)</div>
					</div>
				</div>
			</div>
			<div class="col-md-6 clearfix">
				<div class="sub-header-container right-table">
					<div class="sub-header">Fund Breakdown by Domicile</div>
				</div>
				<div class="right-table">
					<table>
						<thead>
							<tr>
								<td>Regions</td>
								<td class="align-right">Fund</td>
								<td class="align-right last-col">
									<div>
										{{ indexName }}
										<disclosure-superscript
											class="detail-page"
											anchor="newGeo_indexId"
											:statistics="[indexId]"
										/>
										<i-ball-modal :tag="indexId" />
									</div>
								</td>
							</tr>
						</thead>
						<tbody>
							<tr v-for="(row, index) in strategyByDomicile" :key="index">
								<td class>
									{{ row.RegionName }}
									<disclosure-superscript
										class="detail-page"
										anchor="newGeo_cashEquivalents"
										:statistics="['CBD_CE']"
									/>
								</td>
								<td class="data">{{ $format.rawPercent(row.Value, 1) }}</td>
								<td class="data">{{ $format.rawPercent(row.IndexValue, 1) }}</td>
							</tr>
						</tbody>
					</table>
					<div class="sub-info adjust-date">% of net assets as of {{ asOfDate }} (updated monthly)</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
	name: 'GeographyInvesting',
	data() {
		return {
			viewDomicile: true,
			viewRevenue: false
		};
	},
	computed: {
		...mapGetters(['getFundInfoData', 'geographyInvestingData']),
		asOfDate() {
			return (this.widgetData && this.widgetData.asOfDate) || '--';
		},
		domicileChart() {
			return (
				(this.widgetData &&
					this.widgetData.charts &&
					this.widgetData.charts.chartEquityDomicile) ||
				''
			);
		},
		equityByDomicile() {
			return (this.widgetData && this.widgetData.equityByDomicile) || [];
		},
		equityByRevenue() {
			return (this.widgetData && this.widgetData.equityByRevenue) || [];
		},
		indexId() {
			return (
				(this.getFundInfoData && this.getFundInfoData.cgIndexID) || ''
			);
		},
		indexName() {
			return (this.widgetData && this.widgetData.indexName) || '--';
		},
		revenueChart() {
			return (
				(this.widgetData &&
					this.widgetData.charts &&
					this.widgetData.charts.chartEquityRevenue) ||
				''
			);
		},
		strategyByDomicile() {
			return (
				(this.widgetData && this.widgetData.strategyByDomicile) || []
			);
		},
		widgetData() {
			const data = this.geographyInvestingData;
			if (data && this.getFundInfoData) {
				data.indexName = this.getFundInfoData.cgIndexName;
				this.$EventBus.$emit(
					'ExcelData',
					'NewGeoInvestingModule',
					data
				);
			}
			return data;
		}
	},
	methods: {
		changeTab(view) {
			if (view === 'domicile') {
				this.viewDomicile = true;
				this.viewRevenue = false;
			}
			if (view === 'revenue') {
				this.viewDomicile = false;
				this.viewRevenue = true;
			}
		},
		getColorClass(name) {
			const colors = {
				'United States': 'blue-fill',
				Canada: 'light-blue-fill',
				Europe: 'medium-gray-fill',
				Japan: 'light-gray-fill',
				'Asia-Pacific ex. Japan': 'yellow-fill',
				'Emerging Markets': 'light-yellow-fill'
			};
			if (Object.prototype.hasOwnProperty.call(colors, name)) {
				return colors[name];
			}
			return '';
		}
	}
};
</script>

<style lang="less" scoped>
@import '../../assets/css/variables.less';
.geography-investing {
	color: @dark-gray;
	font-family: 'AvenirNextLTW01-Regular', Arial;
	sup {
		font-size: 9px;
		top: 0;
		vertical-align: super;
	}
	.sub-header {
		margin-bottom: 0px;
	}
	.tab-container {
		margin-bottom: 5px;
	}
	.sub-header-container {
		margin-bottom: 15px;
	}
	.adjust-date {
		margin-top: 20px;
	}
	.right-table {
		margin-left: 24px;
	}
	table {
		width: 100%;
		thead {
			td {
				vertical-align: bottom;
				&.first-col {
					width: 94px;
				}
				&.second-col {
					width: 52px;
				}
				&.last-col {
					width: 185px;
					div {
						margin-left: auto;
						width: 125px;
					}
				}
			}
		}
		td {
			.thin-light-gray-border-bottom();
			vertical-align: top;
			padding: 2px 3px;
			&.data {
				text-align: right;
				.thick-font-family();
			}
			&.align-right {
				text-align: right;
			}
			&.square {
				vertical-align: top;
				width: 15px;
			}
			.narrow {
				display: inline-block;
				width: 125px;
			}
		}
	}
	@media (min-width: 481px) {
		img {
			height: 124px;
		}
	}
	@media (max-width: 768px) {
		img {
			margin-top: 25px;
		}
		.sub-header-container {
			margin-top: 50px;
			margin-bottom: 0;
		}
		.adjust-date {
			margin-left: 20px;
		}
	}
	@media (max-width: 480px) {
		.col-xs-4,
		.col-xs-8 {
			width: 100%;
		}
		img {
			width: 100%;
			margin-bottom: 25px;
		}
	}
}
</style>
